import React, { useEffect, useMemo, useState } from "react"
import Grid from "@mui/material/Grid"
import Select from "../select/select"
import DatePicker from "../datePicker/datePicker"
import TextArea from "../textArea/textArea"
import Button from "../button/button"
import IconsColor from "../../util/iconsColor"
import {
    AddFollowUpFormErrors,
    AddFollowUpFormValues,
    buildFollowUpTypeOptionsForQuotation,
    createAddFollowUpFormValuesFromItem,
    createDefaultAddFollowUpFormValues,
    getMinNextFollowUpDate,
    hasAddFollowUpValidationErrors,
    isInternalNoteFollowUpType,
    isNextFollowUpDateAfterFollowUpDate,
    validateAddFollowUpForm,
} from "./activityHistoryUtil"
import type { GenericQuotationFollowUpTypeItem } from "../../models/axiosModel"
import type { IFollowUpItem } from "../quotationList/quotationListTypes"
import styles from "./addFollowUpPanel.module.scss"

type AddFollowUpPanelProps = {
    isOpen: boolean
    followUpTypes: GenericQuotationFollowUpTypeItem[]
    allowAllFollowUpTypes: boolean
    editingFollowUp?: IFollowUpItem | null
    isSaving: boolean
    onClose: () => void
    onSave: (values: AddFollowUpFormValues) => Promise<void>
}

const AddFollowUpPanel: React.FC<AddFollowUpPanelProps> = ({
    isOpen,
    followUpTypes,
    allowAllFollowUpTypes,
    editingFollowUp = null,
    isSaving,
    onClose,
    onSave,
}) => {
    const [formValues, setFormValues] = useState<AddFollowUpFormValues>(createDefaultAddFollowUpFormValues)
    const [validationErrors, setValidationErrors] = useState<AddFollowUpFormErrors>({})
    const [showValidationErrors, setShowValidationErrors] = useState(false)

    const isEditMode = Boolean(editingFollowUp?.quotation_follow_up_id)

    const typeOptions = useMemo(
        () => buildFollowUpTypeOptionsForQuotation(
            followUpTypes,
            allowAllFollowUpTypes,
            editingFollowUp?.quotation_follow_up_type_id,
        ),
        [allowAllFollowUpTypes, editingFollowUp?.quotation_follow_up_type_id, followUpTypes],
    )

    useEffect(() => {
        if (!isOpen) return
        const defaultValues = editingFollowUp
            ? createAddFollowUpFormValuesFromItem(editingFollowUp)
            : createDefaultAddFollowUpFormValues()
        const firstTypeId = typeOptions[0]?.value
        if (!editingFollowUp && firstTypeId) {
            defaultValues.quotation_follow_up_type_id = firstTypeId
        }
        setFormValues(defaultValues)
        setValidationErrors({})
        setShowValidationErrors(false)
    }, [editingFollowUp, isOpen, typeOptions])

    const isInternalNote = isInternalNoteFollowUpType(formValues.quotation_follow_up_type_id)
    const minNextFollowUpDate = useMemo(
        () => getMinNextFollowUpDate(formValues.follow_up_date) || undefined,
        [formValues.follow_up_date],
    )

    if (!isOpen) return null

    const updateField = <K extends keyof AddFollowUpFormValues>(
        key: K,
        value: AddFollowUpFormValues[K],
    ) => {
        setFormValues((prev) => ({ ...prev, [key]: value }))
    }

    const handleTypeChange = (nextTypeId: string) => {
        setFormValues((prev) => ({
            ...prev,
            quotation_follow_up_type_id: nextTypeId,
            next_follow_up_date: isInternalNoteFollowUpType(nextTypeId) ? "" : prev.next_follow_up_date,
        }))
    }

    const handleFollowUpDateChange = (value: string) => {
        setFormValues((prev) => {
            const nextValues = { ...prev, follow_up_date: value }
            if (
                prev.next_follow_up_date
                && !isNextFollowUpDateAfterFollowUpDate(value, prev.next_follow_up_date)
            ) {
                nextValues.next_follow_up_date = ""
            }
            return nextValues
        })
    }

    const handleSave = async () => {
        const errors = validateAddFollowUpForm(formValues)
        if (hasAddFollowUpValidationErrors(errors)) {
            setValidationErrors(errors)
            setShowValidationErrors(true)
            return
        }

        setShowValidationErrors(false)
        setValidationErrors({})
        await onSave(formValues)
    }

    const calendarIcon = (
        <IconsColor
            iconName="calendarIcon"
            width="18px"
            height="18px"
            fillColor="none"
            strokeColor="#6B7280"
        />
    )

    return (
        <div
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-follow-up-title"
        >
                <div className={styles.dragHandle} />
                <h3 id="add-follow-up-title" className={styles.title}>
                    {isEditMode ? "Edit Follow-Up" : "Add Follow-Up"}
                </h3>

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                            <Select
                                label="Type"
                                required={true}
                                value={formValues.quotation_follow_up_type_id}
                                onChange={(event) => handleTypeChange(event.target.value)}
                                options={typeOptions}
                                placeholder="Select type"
                                error={showValidationErrors ? validationErrors.quotation_follow_up_type_id : undefined}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <DatePicker
                                label="Follow-Up Date"
                                required={true}
                                value={formValues.follow_up_date}
                                onChange={handleFollowUpDateChange}
                                leftIcon={calendarIcon}
                                error={showValidationErrors ? validationErrors.follow_up_date : undefined}
                            />
                        </Grid>
                        {!isInternalNote && (
                            <Grid size={{ xs: 12, md: 12 }}>
                                <DatePicker
                                    label="Next Follow-Up Date"
                                    value={formValues.next_follow_up_date}
                                    onChange={(value) => updateField("next_follow_up_date", value)}
                                    leftIcon={calendarIcon}
                                    minDate={minNextFollowUpDate}
                                    clearable={true}
                                    error={showValidationErrors ? validationErrors.next_follow_up_date : undefined}
                                />
                            </Grid>
                        )}
                        <Grid size={{ xs: 12, md: 12 }}>
                            <TextArea
                                label="Remarks"
                                required={true}
                                value={formValues.remarks}
                                onChange={(event) => updateField("remarks", event.target.value)}
                                placeholder="What was discussed / next step..."
                                height="110px"
                                rows={4}
                                error={showValidationErrors ? validationErrors.remarks : undefined}
                            />
                    </Grid>
                </Grid>

                <div className={styles.actions}>
                    <Button
                        type="outlined"
                        label="Cancel"
                        onClick={onClose}
                        disabled={isSaving}
                        style={{ minWidth: "96px", height: "40px" }}
                    />
                    <Button
                        type="contained"
                        label={isEditMode ? "Update Follow-Up" : "Save Follow-Up"}
                        onClick={handleSave}
                        disabled={isSaving}
                        style={{ minWidth: "140px", height: "40px" }}
                    />
                </div>
        </div>
    )
}

export default AddFollowUpPanel
